import { useSearchDoctorsByTimeSlotQuery } from '@/hooks/telemedicine/telemedicine.gql.impl';
import { DiagnosisCategoryType, GeoPoint } from '@/models/diagnosis';
import { EDoctorSortType, NonF2fDoctor, NonF2fDoctorReviewInfo } from '@/models/doctor/telemedicine';
import { DoctorSortType, MedicalCategoryType, SearchDoctorsByTimeSlotQuery } from '@/types/bff/gql-schema';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

interface Props {
  categoryType?: DiagnosisCategoryType;
  categoryId?: string;
  targetDate: string;
  startTimeSlotValue: number;
  endTimeSlotValue: number;
  sortType?: EDoctorSortType;
  specialistOnly?: boolean;
  geoPoint?: GeoPoint;
}

const parseCategoryType = (categoryType?: DiagnosisCategoryType) => {
  switch (categoryType) {
    case DiagnosisCategoryType.Treatment:
      return MedicalCategoryType.Treatment;
    case DiagnosisCategoryType.Department:
    default:
      return MedicalCategoryType.Department;
  }
};

const parseSortType = (sortType?: EDoctorSortType): DoctorSortType => {
  switch (sortType) {
    case EDoctorSortType.DEFAULT:
      return DoctorSortType.Default;
    case EDoctorSortType.DISTANCE:
      return DoctorSortType.Distance;
    case EDoctorSortType.REVIEW_COUNT:
      return DoctorSortType.ReviewCount;
    case EDoctorSortType.REVIEW_AVERAGE_RATING:
      return DoctorSortType.ReviewAverageRating;
    default:
      return DoctorSortType.Default;
  }
};

const parseSearchDoctors = (data?: InfiniteData<SearchDoctorsByTimeSlotQuery>): NonF2fDoctor[] | undefined => {
  return data?.pages.flatMap((page) =>
    page.searchDoctorsByTimeSlot.items.map((doctor) => {
      return new NonF2fDoctor(
        doctor.hospitalName,
        doctor.publicId,
        doctor.name,
        doctor.medicineType,
        doctor.profileUrl ?? null,
        doctor.readyForVideoCall,
        doctor.professionalDepartment?.name ?? null,
        new NonF2fDoctorReviewInfo(doctor.reviewAverageRating, doctor.reviewCount),
        null,
        null
      );
    })
  );
};

const SEARCH_DOCTORS_LIMIT = 10;

const useSearchDoctorsInfinity = ({
  categoryType,
  categoryId = '',
  targetDate,
  startTimeSlotValue,
  endTimeSlotValue,
  sortType,
  specialistOnly = false,
  geoPoint,
}: Props) => {
  const parsedSortType = parseSortType(sortType);

  const {
    data: searchDoctors,
    isLoading: isLoadingSearchDoctors,
    fetchNextPage: fetchNextPageSearchDoctors,
    hasNextPage: hasNextPageSearchDoctors = false,
  } = useInfiniteQuery<SearchDoctorsByTimeSlotQuery>(
    useSearchDoctorsByTimeSlotQuery.getKey({
      request: {
        condition: {
          medicalCategory: {
            category: parseCategoryType(categoryType),
            id: categoryId,
          },
          specialistOnly,
          targetDate,
          startTimeSlotValue,
          endTimeSlotValue,
          geoPoint: geoPoint
            ? {
                latitude: geoPoint.latitude,
                longitude: geoPoint.longitude,
              }
            : undefined,
        },
        page: 1,
        limit: SEARCH_DOCTORS_LIMIT,
        sortType: parsedSortType,
      },
    }),
    ({ pageParam }) => {
      const requestPage = pageParam ?? 1;
      return useSearchDoctorsByTimeSlotQuery.fetcher({
        request: {
          condition: {
            medicalCategory: {
              category: parseCategoryType(categoryType),
              id: categoryId,
            },
            specialistOnly,
            targetDate,
            startTimeSlotValue,
            endTimeSlotValue,
            geoPoint: geoPoint
              ? {
                  latitude: geoPoint.latitude,
                  longitude: geoPoint.longitude,
                }
              : undefined,
          },
          page: requestPage,
          limit: SEARCH_DOCTORS_LIMIT,
          sortType: parsedSortType,
        },
      })();
    },
    {
      enabled: !!categoryId && !!categoryType && !!targetDate && startTimeSlotValue >= 0 && endTimeSlotValue >= 0,
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.searchDoctorsByTimeSlot.items.length === SEARCH_DOCTORS_LIMIT) {
          return pages.length + 1;
        }
        return undefined;
      },
    }
  );

  return {
    searchDoctors: parseSearchDoctors(searchDoctors),
    isLoadingSearchDoctors,
    fetchNextPageSearchDoctors,
    hasNextPageSearchDoctors,
  };
};

export default useSearchDoctorsInfinity;
